import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ThemedButton } from "@/components/themed";
import ResponsiveImage from "@/components/ResponsiveImage";
import {
  fetchKujiCheckoutPreview,
  submitKujiCheckoutWithCard,
  type KujiCheckoutPreview,
  type KujiCheckoutResult,
} from "@/services/customerService";
import { Input } from "@/components/ui/input";
import LotteryAnimation from "@/components/LotteryAnimation";
import CardRegistrationDialog from "@/components/CardRegistrationDialog";
import AddressRegistrationDialog from "@/components/AddressRegistrationDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronLeft, CreditCard, MapPin, Package, Loader2, CheckCircle2, Coins } from "lucide-react";

type KujiCheckoutPageProps = {
  siteId: number;
};

type PaymentMethodType = "card";

const KujiCheckoutPage: React.FC<KujiCheckoutPageProps> = ({ siteId }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<KujiCheckoutPreview | null>(null);
  const [result, setResult] = useState<KujiCheckoutResult | null>(null);
  const [pendingResult, setPendingResult] = useState<KujiCheckoutResult | null>(null);
  const [animationPhase, setAnimationPhase] = useState(false);
  const [animationIndex, setAnimationIndex] = useState(0);

  // フォーム状態
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [quantityMode, setQuantityMode] = useState<"custom" | number>("custom");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>("card");
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [pointsToUse, setPointsToUse] = useState<number>(0);
  const [usePoints, setUsePoints] = useState(false);

  // 数量選択肢（kuji_configの設定に基づく）
  const minQuantity = preview?.kuji_config?.min_quantity ?? 1;
  const configMax = preview?.kuji_config?.max_quantity ?? 100;
  const remainingStock = preview?.kuji_config?.remaining_stock ?? configMax;
  const maxQuantity = Math.min(configMax, remainingStock);
  const bulkQuantities = preview?.kuji_config?.bulk_quantities ?? [];
  const quantityOptions = Array.from(
    { length: Math.max(maxQuantity - minQuantity + 1, 0) },
    (_, i) => minQuantity + i
  );

  // カード決済が有効か
  const cardPaymentEnabled = preview?.card_payment_enabled ?? false;
  const paymentMethods = preview?.payment_methods ?? [];
  const hasValidCard = paymentMethods.some((pm) => !pm.expired);

  // 配送料/システム手数料のラベル
  const isSystemFee = preview?.event?.shipping_fee_label === 'system_fee';
  const feeLabel = isSystemFee ? 'システム手数料' : '配送料';
  const freeFeeLabel = isSystemFee ? '手数料無料' : '送料無料';

  // ポイント関連
  const currentBalance = preview?.current_balance ?? 0;
  const maxPointsUsable = Math.min(currentBalance, preview?.cost?.total ?? 0);
  const cardAmount = Math.max(0, (preview?.cost?.total ?? 0) - pointsToUse);
  const isFullPointPayment = cardAmount === 0 && pointsToUse > 0;

  // プレビュー読み込み
  useEffect(() => {
    const loadPreview = async () => {
      if (!eventId) return;
      setLoading(true);
      try {
        const data = await fetchKujiCheckoutPreview(siteId, eventId, quantity);

        // 売り切れの場合はイベント詳細にリダイレクト
        // remaining_stock が null の場合は無制限（固定確率モード）なのでリダイレクトしない
        const remainingStock = data.kuji_config?.remaining_stock;
        if (remainingStock !== null && remainingStock !== undefined && remainingStock <= 0) {
          navigate(`/kuji/${eventId}`, { replace: true });
          return;
        }

        setPreview(data);
        // 数量を最小値に設定
        const min = data.kuji_config?.min_quantity ?? 1;
        setQuantity(min);
        // デフォルト住所を選択
        const defaultAddr = data.addresses.find((a) => a.default) || data.addresses[0];
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.id);
        }
        // デフォルトカードを選択
        if (data.payment_methods && data.payment_methods.length > 0) {
          const defaultCard = data.payment_methods.find((pm) => pm.is_default && !pm.expired) ||
            data.payment_methods.find((pm) => !pm.expired);
          if (defaultCard) {
            setSelectedCardId(defaultCard.id);
            setPaymentMethod("card");
          }
        }
      } catch (err: any) {
        setError(err.message || "データの読み込みに失敗しました");
      } finally {
        setLoading(false);
      }
    };
    loadPreview();
  }, [siteId, eventId]);

  // 数量変更時にコストを再計算（くじ代 + 配送料）
  useEffect(() => {
    if (!preview) return;
    const kujiCost = preview.event.gacha_cost * quantity;
    // 配送料を計算: 無料設定の場合は0、それ以外はceil(quantity / tier_quantity) * fee_per_tier
    let shippingFee = 0;
    if (!preview.kuji_config?.free_shipping) {
      const tierQty = preview.kuji_config?.shipping_fee_tier_quantity || 20;
      const feePerTier = preview.kuji_config?.shipping_fee_per_tier || 0;
      shippingFee = tierQty > 0 ? Math.ceil(quantity / tierQty) * feePerTier : 0;
    }
    const newCost = {
      unit_price: preview.event.gacha_cost,
      quantity: quantity,
      kuji_cost: kujiCost,
      shipping_fee: shippingFee,
      total: kujiCost + shippingFee,
    };
    setPreview((prev) => (prev ? { ...prev, cost: newCost } : null));
  }, [quantity]);

  // 数量変更時にポイント利用数を調整（最大利用可能を超えないように）
  useEffect(() => {
    if (!preview) return;
    const newMaxPoints = Math.min(preview.current_balance, preview.cost.total);
    if (pointsToUse > newMaxPoints) {
      setPointsToUse(newMaxPoints);
    }
  }, [preview?.cost.total, preview?.current_balance]);

  // ポイント使用チェックボックスがオフの時はポイントを0にリセット
  useEffect(() => {
    if (!usePoints) {
      setPointsToUse(0);
    } else if (preview) {
      // チェックをオンにした時、全額利用をデフォルトに
      const maxPoints = Math.min(preview.current_balance, preview.cost.total);
      setPointsToUse(maxPoints);
    }
  }, [usePoints]);

  // 購入実行（カード決済 または 全額ポイント払い）
  const handleSubmit = async () => {
    if (!eventId || !selectedAddressId) return;
    // 全額ポイント払いでない場合はカード必須
    if (!isFullPointPayment && !selectedCardId) return;

    setSubmitting(true);
    setError(null);

    try {
      const isBulkPurchase = typeof quantityMode === "number";

      const cardResult = await submitKujiCheckoutWithCard(
        siteId,
        eventId,
        quantity,
        selectedAddressId,
        isFullPointPayment ? null : selectedCardId,
        isBulkPurchase,
        pointsToUse
      );

      // 全額ポイント払いで成功した場合
      if (cardResult.full_point_payment && cardResult.success && cardResult.results && cardResult.summary) {
        const fullPointResult: KujiCheckoutResult = {
          success: true,
          results: cardResult.results,
          summary: cardResult.summary,
          new_balance: 0,
        };
        // 等級ごとのアニメーションまたはイベント全体のアニメーションがあれば再生
        const hasAnyAnimation = preview.event.lottery_animation_url || cardResult.results.some((r: any) => r.grade_animation_url);
        if (hasAnyAnimation) {
          setPendingResult(fullPointResult);
          setAnimationIndex(0);
          setAnimationPhase(true);
        } else {
          setResult(fullPointResult);
        }
        return;
      }

      if (cardResult.requires_action && cardResult.redirect_url) {
        // 3DS 認証画面にリダイレクト
        window.location.href = cardResult.redirect_url;
        return;
      } else if (cardResult.success && cardResult.payment_uid) {
        // 3DS 不要で決済完了 → 結果ページへ遷移
        navigate(`/kuji/card-result?status=success&payment_uid=${cardResult.payment_uid}`);
        return;
      }
    } catch (err: any) {
      setError(err.message || "購入に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  // 購入可能チェック
  const canSubmit =
    !submitting &&
    selectedAddressId &&
    quantity > 0 &&
    agreedToTerms &&
    (isFullPointPayment || selectedCardId !== null);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-theme-bg">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error && !preview) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-theme-bg">
        <p className="text-red-600 mb-4">{error}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          戻る
        </Button>
      </div>
    );
  }

  if (!preview) return null;

  // アニメーション再生フェーズ
  if (animationPhase && pendingResult) {
    const totalAnimations = pendingResult.results.length;

    const handleAnimationEnd = () => {
      const nextIndex = animationIndex + 1;
      if (nextIndex >= totalAnimations) {
        setAnimationPhase(false);
        setResult(pendingResult);
        setPendingResult(null);
      } else {
        setAnimationIndex(nextIndex);
      }
    };

    const handleSkipAll = () => {
      setAnimationPhase(false);
      setResult(pendingResult);
      setPendingResult(null);
    };

    // 等級ごとのアニメーションURLを優先、なければイベント全体のアニメーションにフォールバック
    const currentResult = pendingResult.results[animationIndex];
    const currentAnimationUrl = currentResult?.grade_animation_url || preview.event.lottery_animation_url;

    return (
      <LotteryAnimation
        animationUrl={currentAnimationUrl}
        isPlaying={true}
        onAnimationEnd={handleAnimationEnd}
        onSkipAll={handleSkipAll}
        gachaCount={totalAnimations}
        currentIndex={animationIndex}
        totalCount={totalAnimations}
      />
    );
  }

  // 購入完了画面
  if (result) {
    return (
      <>
        <Helmet>
          <title>購入完了</title>
        </Helmet>
        <div className="max-w-screen-md mx-auto px-4 py-8 pt-24 bg-theme-bg min-h-screen">
          <div className="bg-white rounded-lg border-2 border-black p-6 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">購入が完了しました！</h1>
            <p className="text-gray-600 mb-6">
              {result.summary.total_quantity}枚のスクラッチを引きました。マイコレクションから発送依頼またはPT交換が可能です。
            </p>

            {/* 結果サマリー */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h2 className="font-bold mb-3">当選結果</h2>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {result.results.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 bg-white rounded border">
                    {item.image_url && (
                      <img src={item.image_url} alt={item.product_name} className="w-12 h-12 object-cover rounded" />
                    )}
                    <div>
                      <span className={`text-xs font-bold ${item.grade_name === 'セット購入特典' ? 'text-blue-600' : 'text-orange-600'}`}>{item.grade_name}</span>
                      <p className="text-sm font-medium">{item.product_name || "景品"}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 支払い情報 */}
            <div className="bg-orange-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex justify-between text-sm mb-1">
                <span>スクラッチ代</span>
                <span>¥{result.summary.kuji_cost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span>{feeLabel}</span>
                {result.summary.shipping_fee === 0 ? (
                  <span className="text-green-600 font-bold">{freeFeeLabel}</span>
                ) : (
                  <span>¥{result.summary.shipping_fee.toLocaleString()}</span>
                )}
              </div>
              {(result.summary.points_used ?? 0) > 0 && (
                <>
                  <div className="flex justify-between text-sm mb-1 border-t pt-1">
                    <span>ポイント利用</span>
                    <span className="text-orange-600">-{result.summary.points_used?.toLocaleString()}pt</span>
                  </div>
                  {(result.summary.card_amount ?? 0) > 0 && (
                    <div className="flex justify-between text-sm mb-1">
                      <span>カード決済</span>
                      <span>¥{result.summary.card_amount?.toLocaleString()}</span>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between font-bold border-t pt-2">
                <span>お支払い</span>
                <div className="text-right">
                  {(result.summary.points_used ?? 0) > 0 && (
                    <span className="text-orange-600 mr-1">{result.summary.points_used?.toLocaleString()}pt</span>
                  )}
                  {(result.summary.card_amount ?? 0) > 0 && (
                    <span className="text-orange-600">¥{result.summary.card_amount?.toLocaleString()}</span>
                  )}
                  {(result.summary.points_used ?? 0) === 0 && (result.summary.card_amount ?? 0) === 0 && (
                    <span className="text-orange-600">¥{result.summary.total_paid.toLocaleString()}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Link to="/products">
                <ThemedButton className="w-full">マイコレクションを見る</ThemedButton>
              </Link>
              <Link to={`/kuji/${eventId}`}>
                <Button variant="outline" className="w-full">
                  もう一度引く
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>購入確認 | {preview.event.name}</title>
      </Helmet>

      <div className="max-w-screen-xl mx-auto px-4 py-6 pt-20 bg-theme-bg min-h-screen">
        {/* 戻るリンク */}
        <Link
          to={`/kuji/${eventId}`}
          className="inline-flex items-center text-sm text-gray-600 hover:text-black mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          スクラッチ詳細に戻る
        </Link>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* 左カラム: フォーム */}
          <div className="flex-1 space-y-6">
            {/* 数量選択 */}
            <div className="bg-white rounded-lg border-2 border-black p-5">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                数量選択
              </h2>

              <RadioGroup
                value={quantityMode.toString()}
                onValueChange={(v) => {
                  if (v === "custom") {
                    setQuantityMode("custom");
                  } else {
                    const bulkQty = parseInt(v);
                    setQuantityMode(bulkQty);
                    setQuantity(bulkQty);
                  }
                }}
                className="space-y-3"
              >
                {/* カスタム数量選択 */}
                <div
                  className={`p-3 border-2 rounded-lg cursor-pointer ${
                    quantityMode === "custom" ? "border-orange-500 bg-orange-50" : "border-black hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="custom" id="q-custom" />
                    <Label htmlFor="q-custom" className="cursor-pointer flex items-center gap-2 flex-1">
                      <Select
                        value={quantity.toString()}
                        onValueChange={(v) => {
                          setQuantity(parseInt(v));
                          setQuantityMode("custom");
                        }}
                      >
                        <SelectTrigger className="w-20 border-2 border-black">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {quantityOptions.map((n) => (
                            <SelectItem key={n} value={n.toString()}>
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span>枚</span>
                      <span className="text-gray-500 text-sm">
                        (¥{(preview.event.gacha_cost * (quantityMode === "custom" ? quantity : 0)).toLocaleString()})
                      </span>
                    </Label>
                  </div>
                </div>

                {/* まとめ買いオプション */}
                {bulkQuantities.map((bulkQty) => {
                  const remainingStock = preview?.kuji_config?.remaining_stock ?? Infinity;
                  const isStockInsufficient = bulkQty > remainingStock;
                  const bulkBonus = preview?.kuji_config?.bulk_bonuses?.find(b => b.quantity === bulkQty);
                  return (
                    <div
                      key={bulkQty}
                      className={`p-3 border-2 rounded-lg ${
                        isStockInsufficient
                          ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                          : quantityMode === bulkQty
                            ? "border-orange-500 bg-orange-50 cursor-pointer"
                            : "border-black hover:bg-gray-50 cursor-pointer"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value={bulkQty.toString()} id={`q-bulk-${bulkQty}`} disabled={isStockInsufficient} />
                        <Label htmlFor={`q-bulk-${bulkQty}`} className={`flex-1 ${isStockInsufficient ? "cursor-not-allowed" : "cursor-pointer"}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold">{bulkQty}枚まとめ買い</span>
                            <span className="text-gray-500">
                              (¥{(preview.event.gacha_cost * bulkQty).toLocaleString()})
                            </span>
                            {bulkBonus?.product && bulkBonus.label && (
                              <span className="px-2 py-0.5 text-xs font-bold text-red-600 border border-red-500 rounded">
                                {bulkBonus.label}
                              </span>
                            )}
                          </div>
                          {isStockInsufficient && (
                            <span className="block text-xs text-red-500 mt-1">在庫不足</span>
                          )}
                        </Label>
                      </div>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>

            {/* 支払い方法 */}
            <div className="bg-white rounded-lg border-2 border-black p-5">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                支払い方法選択
              </h2>

              <RadioGroup
                value={paymentMethod}
                onValueChange={(v) => setPaymentMethod(v as PaymentMethodType)}
                className="space-y-3"
              >
                {/* カード払い */}
                {cardPaymentEnabled && (
                  <div
                    className={`p-3 border-2 rounded-lg ${
                      !hasValidCard
                        ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                        : paymentMethod === "card"
                          ? "border-orange-500 bg-orange-50 cursor-pointer"
                          : "border-black hover:bg-gray-50 cursor-pointer"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="card" id="pay-card" disabled={!hasValidCard} />
                      <Label htmlFor="pay-card" className={`flex-1 ${!hasValidCard ? "cursor-not-allowed" : "cursor-pointer"}`}>
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          <span className="font-bold">カード払い</span>
                        </div>
                        {paymentMethods.length > 0 ? (
                          <div className="mt-2 space-y-1">
                            {paymentMethods.map((pm) => (
                              <div
                                key={pm.id}
                                className={`flex items-center gap-2 text-sm ${pm.expired ? "text-red-500 line-through" : "text-gray-600"}`}
                                onClick={(e) => {
                                  if (!pm.expired) {
                                    e.stopPropagation();
                                    setSelectedCardId(pm.id);
                                    setPaymentMethod("card");
                                  }
                                }}
                              >
                                <input
                                  type="radio"
                                  name="card-select"
                                  checked={selectedCardId === pm.id}
                                  onChange={() => {
                                    setSelectedCardId(pm.id);
                                    setPaymentMethod("card");
                                  }}
                                  disabled={pm.expired}
                                  className="w-3 h-3"
                                />
                                <span>{pm.brand} •••• {pm.last4}</span>
                                <span className="text-xs text-gray-400">
                                  {String(pm.exp_month).padStart(2, "0")}/{pm.exp_year}
                                </span>
                                {pm.expired && <span className="text-xs text-red-500">期限切れ</span>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 mt-1">カードが登録されていません</p>
                        )}
                        {!hasValidCard && paymentMethods.length > 0 && (
                          <p className="text-xs text-red-500 mt-1">有効なカードがありません</p>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCardDialog(true);
                          }}
                          className="mt-2 text-sm text-blue-600 underline"
                        >
                          カードを変更する
                        </button>
                      </Label>
                    </div>
                  </div>
                )}

              </RadioGroup>

              {paymentMethod === "card" && !hasValidCard && cardPaymentEnabled && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-700 text-sm mb-2">
                    カード決済を利用するには、カードを登録してください。
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowCardDialog(true)}
                    className="text-sm text-blue-600 underline"
                  >
                    カードを登録する
                  </button>
                </div>
              )}

              {/* ポイント利用 */}
              {currentBalance > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="use-points"
                      checked={usePoints}
                      onCheckedChange={(checked) => setUsePoints(checked === true)}
                      className="h-5 w-5 rounded-none border-2 border-gray-400 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    <label htmlFor="use-points" className="flex items-center gap-2 cursor-pointer">
                      <Coins className="w-4 h-4 text-orange-500" />
                      <span className="font-bold">ポイントを</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={pointsToUse}
                        onChange={(e) => {
                          const numValue = e.target.value.replace(/[^0-9]/g, '');
                          const value = Math.min(
                            Math.max(0, parseInt(numValue) || 0),
                            maxPointsUsable
                          );
                          setPointsToUse(value);
                          if (value > 0) setUsePoints(true);
                        }}
                        disabled={!usePoints}
                        className="w-24 border-2 border-black text-right h-8 disabled:opacity-50"
                      />
                      <span className="font-bold">pt 使う</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 ml-8">
                    保有: {currentBalance.toLocaleString()}pt
                  </p>
                  {isFullPointPayment && (
                    <div className="mt-2 ml-8 p-2 bg-green-50 border border-green-200 rounded">
                      <p className="text-green-700 text-sm">
                        全額ポイントでお支払い。カード決済は不要です。
                      </p>
                    </div>
                  )}
                  {usePoints && !isFullPointPayment && cardAmount > 0 && (
                    <p className="text-sm text-gray-600 mt-2 ml-8">
                      → カード決済額: <span className="font-bold text-orange-600">¥{cardAmount.toLocaleString()}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* お届け先 */}
            <div className="bg-white rounded-lg border-2 border-black p-5">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                お届け先
              </h2>

              {preview.addresses.length > 0 ? (
                <>
                  <RadioGroup
                    value={selectedAddressId?.toString() || ""}
                    onValueChange={(v) => setSelectedAddressId(parseInt(v))}
                    className="space-y-3"
                  >
                    {preview.addresses.map((addr) => (
                      <div
                        key={addr.id}
                        className={`p-3 border-2 rounded-lg cursor-pointer ${
                          selectedAddressId === addr.id ? "border-orange-500 bg-orange-50" : "border-black hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem value={addr.id.toString()} id={`addr-${addr.id}`} className="mt-1" />
                          <Label htmlFor={`addr-${addr.id}`} className="cursor-pointer flex-1">
                            <p className="font-bold">{addr.name}</p>
                            <p className="text-sm text-gray-600">
                              〒{addr.postal_code} {addr.address_line1}
                              {addr.address_line2 && ` ${addr.address_line2}`}
                            </p>
                            <p className="text-sm text-gray-600">{addr.phone_number}</p>
                            {addr.default && (
                              <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-gray-200 rounded">デフォルト</span>
                            )}
                          </Label>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                  <button
                    type="button"
                    onClick={() => setShowAddressDialog(true)}
                    className="mt-3 text-sm text-blue-600 underline"
                  >
                    お届け先を変更する
                  </button>
                </>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-500 mb-3">お届け先が登録されていません</p>
                  <Button
                    variant="outline"
                    className="border-2 border-black"
                    onClick={() => setShowAddressDialog(true)}
                  >
                    お届け先を登録
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* 右カラム: サマリー */}
          <div className="lg:w-96">
            <div className="bg-white rounded-lg border-2 border-black p-5 sticky top-24">
              {/* くじ情報 */}
              <div className="mb-4">
                {preview.event.thumbnail_url && (
                  <ResponsiveImage
                    src={preview.event.thumbnail_url}
                    alt={preview.event.name}
                    className="w-full aspect-video object-cover rounded border-2 border-black mb-3"
                    displaySize="medium"
                  />
                )}
                <h3 className="font-bold">{preview.event.name}</h3>
                {preview.event.delivery_estimate && (
                  <p className="text-xs text-gray-500 mt-1">お届け目安: {preview.event.delivery_estimate}</p>
                )}
              </div>

              {/* 内訳 */}
              <div className="border-t-2 border-black pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>スクラッチ代</span>
                  <span>
                    ¥{preview.cost.unit_price.toLocaleString()} × {quantity}枚 = ¥{preview.cost.kuji_cost.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>{feeLabel}</span>
                  {preview.kuji_config?.free_shipping ? (
                    <span className="text-green-600 font-bold">{freeFeeLabel}</span>
                  ) : (
                    <span>¥{preview.cost.shipping_fee.toLocaleString()}</span>
                  )}
                </div>
                <div className="flex justify-between text-sm font-bold border-t pt-2">
                  <span>小計</span>
                  <span>¥{preview.cost.total.toLocaleString()}</span>
                </div>
              </div>

              {/* ポイント利用 */}
              {pointsToUse > 0 && (
                <div className="flex justify-between text-sm mt-2">
                  <span>ポイント利用</span>
                  <span className="text-orange-600">-{pointsToUse.toLocaleString()}pt</span>
                </div>
              )}

              {/* 合計 */}
              <div className="border-t-2 border-black mt-4 pt-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold">お支払い</span>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-orange-600">
                      ¥{cardAmount.toLocaleString()}
                    </span>
                  </div>
                </div>
                {pointsToUse > 0 && (
                  <p className="text-xs text-gray-500 text-right">
                    ({pointsToUse.toLocaleString()}pt 利用)
                  </p>
                )}
              </div>

              {/* 利用規約同意 */}
              <div className="flex items-center gap-2 mt-4">
                <Checkbox
                  id="terms-agreement"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                  className="h-4 w-4 rounded-none border-2 border-gray-400 data-[state=checked]:bg-theme-primary data-[state=checked]:border-theme-primary"
                />
                <label htmlFor="terms-agreement" className="text-sm text-gray-700 cursor-pointer">
                  購入にあたり、<a href="/terms" target="_blank" className="text-theme-primary underline">利用規約</a>に同意します。
                </label>
              </div>

              {/* 購入ボタン */}
              <ThemedButton
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="w-full mt-4 py-4 text-lg font-bold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    処理中...
                  </>
                ) : isFullPointPayment ? (
                  <>ポイントで購入する</>
                ) : pointsToUse > 0 ? (
                  <>ポイント + カードで購入する</>
                ) : (
                  <>カードで購入する</>
                )}
              </ThemedButton>

              {error && <p className="mt-3 text-red-600 text-sm text-center">{error}</p>}
            </div>
          </div>
        </div>
      </div>

      <CardRegistrationDialog
        isOpen={showCardDialog}
        onClose={() => setShowCardDialog(false)}
        onSuccess={async () => {
          if (!eventId) return;
          try {
            const data = await fetchKujiCheckoutPreview(siteId, eventId, quantity);
            setPreview(data);
            // 新しいカードを自動選択
            if (data.payment_methods && data.payment_methods.length > 0) {
              const validCard = data.payment_methods.find((pm) => !pm.expired);
              if (validCard) {
                setSelectedCardId(validCard.id);
                setPaymentMethod("card");
              }
            }
          } catch (err) {}
        }}
        paymentProvider={preview?.payment_provider}
        gmoPgShopId={preview?.gmo_pg_shop_id}
        testMode={preview?.test_mode}
      />

      <AddressRegistrationDialog
        isOpen={showAddressDialog}
        onClose={() => setShowAddressDialog(false)}
        onSuccess={async () => {
          if (!eventId) return;
          try {
            const data = await fetchKujiCheckoutPreview(siteId, eventId, quantity);
            setPreview(data);
            // 新しい住所を自動選択
            if (data.addresses.length > 0) {
              const newAddr = data.addresses[data.addresses.length - 1];
              setSelectedAddressId(newAddr.id);
            }
          } catch (err) {}
        }}
      />
    </>
  );
};

export default KujiCheckoutPage;
